/**
 * Choose how to attach an image (item #10).
 *
 * For a UPI confirmation, a screenshot is usually already on the phone, so a
 * live-camera-only flow is the wrong shape. This offers three sources — take a
 * photo, pick from the gallery, or paste from the clipboard — all producing the
 * same CompressedImage the camera does, so the upload downstream is unchanged.
 *
 * Paste is shown only where the browser actually supports reading an image from
 * the clipboard, so it never appears as a dead control. (Deliberately NOT used
 * for the POD evidence photo, which must be taken live at the door.)
 */

import { useState } from 'react';
import { Sheet, Button, useToast } from './ui';
import { CameraSheet } from './Camera';
import { pickImage, compress, type CompressedImage, type CompressOptions } from '../lib/camera';

export function ImageCaptureSheet({
  open, onClose, onCapture, title = 'Add a photo', facing = 'environment', compressOptions,
}: {
  open: boolean;
  onClose: () => void;
  onCapture: (img: CompressedImage) => void;
  title?: string;
  facing?: 'environment' | 'user';
  compressOptions?: CompressOptions;
}) {
  const toast = useToast();
  const [cameraOpen, setCameraOpen] = useState(false);

  const canPaste = typeof navigator !== 'undefined'
    && !!navigator.clipboard && typeof navigator.clipboard.read === 'function';

  async function fromGallery() {
    const files = await pickImage({ fromCamera: false });
    const file = files[0];
    if (!file) return;
    try {
      onCapture(await compress(file, compressOptions));
      onClose();
    } catch {
      toast.show('Could not read that image. Try another.', 'danger');
    }
  }

  async function fromPaste() {
    try {
      const items = await navigator.clipboard.read();
      for (const item of items) {
        const type = item.types.find((t) => t.startsWith('image/'));
        if (type) {
          const blob = await item.getType(type);
          onCapture(await compress(blob, compressOptions));
          onClose();
          return;
        }
      }
      toast.show('No image on the clipboard — copy the screenshot first.', 'danger');
    } catch {
      toast.show('Paste is not available here — use the gallery.', 'danger');
    }
  }

  // The live camera, when that source is chosen.
  if (cameraOpen) {
    return (
      <CameraSheet
        open
        facing={facing}
        title={title}
        compressOptions={compressOptions}
        onCapture={(img) => { onCapture(img); setCameraOpen(false); onClose(); }}
        onClose={() => setCameraOpen(false)}
      />
    );
  }

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={title}
      footer={<Button variant="ghost" block onClick={onClose}>Cancel</Button>}
    >
      <div className="stack-2">
        <Button block onClick={() => setCameraOpen(true)}>Take photo</Button>
        <Button block variant="ghost" onClick={() => void fromGallery()}>Choose from gallery</Button>
        {canPaste && (
          <Button block variant="ghost" onClick={() => void fromPaste()}>Paste from clipboard</Button>
        )}
        <div className="t-xs t-muted" style={{ textAlign: 'center' }}>
          A WhatsApp screenshot is in your gallery — pick it there, or copy it and tap Paste.
        </div>
      </div>
    </Sheet>
  );
}
